/**
 * Admin — prix unitaires des énergies (éditables sans redéploiement).
 *
 * Édite l'abonnement annuel et le prix €/kWh par énergie, plus l'année de
 * référence et la tolérance de fourchette. À l'enregistrement, le backend
 * reconstruit le bloc complet (tranches) et le stocke en surcharge runtime.
 * Le bouton « Réinitialiser » supprime la surcharge → retour au YAML.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/Layout";
import {
  getEnergyPrices,
  setEnergyPrices,
  resetEnergyPrices,
  type EditableEnergy,
} from "@/api/admin";

export default function AdminEnergyPricesPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "energy-prices"],
    queryFn: getEnergyPrices,
  });

  // État local éditable, hydraté depuis la réponse serveur.
  const [refYear, setRefYear] = useState("");
  const [tolerance, setTolerance] = useState("");
  const [energies, setEnergies] = useState<EditableEnergy[]>([]);

  useEffect(() => {
    if (!data) return;
    setRefYear(String(data.reference_year ?? ""));
    setTolerance(String(data.range_tolerance_pct ?? ""));
    setEnergies(data.energies.map((e) => ({ ...e })));
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      setEnergyPrices({
        reference_year: Number(refYear),
        range_tolerance_pct: Number(tolerance),
        last_updated: new Date().toISOString().slice(0, 10),
        energies: energies.map(({ label: _label, ...rest }) => rest),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "energy-prices"] }),
  });

  const reset = useMutation({
    mutationFn: resetEnergyPrices,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "energy-prices"] }),
  });

  const patch = (key: string, field: keyof EditableEnergy, value: number) =>
    setEnergies((prev) =>
      prev.map((e) => (e.key === key ? { ...e, [field]: value } : e)),
    );

  const patchKva = (key: string, kva: string, value: number) =>
    setEnergies((prev) =>
      prev.map((e) =>
        e.key === key
          ? { ...e, abonnement_par_kva: { ...(e.abonnement_par_kva ?? {}), [kva]: value } }
          : e,
      ),
    );

  return (
    <Layout>
      <div className="mx-auto max-w-3xl px-6 py-12">
        <Link to="/admin" className="text-sm text-blue-900 underline">
          ← Admin
        </Link>
        <h1 className="mt-3 text-2xl font-bold">Prix de l'énergie</h1>
        <p className="mt-1 text-sm text-gray-600">
          Tarifs TTC servant à estimer la facture annuelle et les profils
          d'usage. À mettre à jour ≥ 1×/an (l'électricité évolue 2×/an, le gaz
          mensuellement).
        </p>

        {isLoading && <p className="mt-6 text-sm text-gray-500">Chargement…</p>}
        {error && (
          <p className="mt-6 text-sm text-red-700">Impossible de charger les prix.</p>
        )}

        {data && (
          <>
            <div className="mt-6 flex items-center gap-2 text-xs">
              <span
                className={`rounded-full px-2.5 py-1 font-semibold ${
                  data.is_override
                    ? "bg-amber-100 text-amber-800"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {data.is_override ? "Valeurs personnalisées (surcharge active)" : "Valeurs par défaut (YAML)"}
              </span>
              {data.last_updated && (
                <span className="text-gray-400">MAJ {data.last_updated}</span>
              )}
            </div>

            {/* Globaux */}
            <section className="mt-5 grid grid-cols-2 gap-3">
              <label className="text-sm">
                <span className="text-gray-600">Année de référence</span>
                <input
                  type="number"
                  value={refYear}
                  onChange={(e) => setRefYear(e.target.value)}
                  className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5"
                />
              </label>
              <label className="text-sm">
                <span className="text-gray-600">Fourchette ± (%)</span>
                <input
                  type="number"
                  step="0.1"
                  value={tolerance}
                  onChange={(e) => setTolerance(e.target.value)}
                  className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5"
                />
              </label>
            </section>

            {/* Énergies */}
            <section className="mt-6 space-y-3">
              {energies.map((e) => (
                <div key={e.key} className="rounded-lg border border-gray-200 bg-white p-4">
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-gray-800">{e.label}</div>
                    <code className="text-xs text-gray-400">{e.key}</code>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <label className="text-sm">
                      <span className="text-gray-600">Abonnement (€/an)</span>
                      <input
                        type="number"
                        step="0.01"
                        value={e.abonnement_annuel_eur}
                        onChange={(ev) =>
                          patch(e.key, "abonnement_annuel_eur", Number(ev.target.value))
                        }
                        className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5"
                      />
                    </label>
                    {e.eur_per_kwh != null && (
                      <label className="text-sm">
                        <span className="text-gray-600">Prix (€/kWh)</span>
                        <input
                          type="number"
                          step="0.0001"
                          value={e.eur_per_kwh}
                          onChange={(ev) =>
                            patch(e.key, "eur_per_kwh", Number(ev.target.value))
                          }
                          className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5"
                        />
                      </label>
                    )}
                    {e.eur_per_kwh_hc != null && (
                      <label className="text-sm">
                        <span className="text-gray-600">Prix HC (€/kWh)</span>
                        <input
                          type="number"
                          step="0.0001"
                          value={e.eur_per_kwh_hc}
                          onChange={(ev) =>
                            patch(e.key, "eur_per_kwh_hc", Number(ev.target.value))
                          }
                          className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5"
                        />
                      </label>
                    )}
                    {e.eur_per_kwh_hp != null && (
                      <label className="text-sm">
                        <span className="text-gray-600">Prix HP (€/kWh)</span>
                        <input
                          type="number"
                          step="0.0001"
                          value={e.eur_per_kwh_hp}
                          onChange={(ev) =>
                            patch(e.key, "eur_per_kwh_hp", Number(ev.target.value))
                          }
                          className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5"
                        />
                      </label>
                    )}
                  </div>

                  {e.abonnement_par_kva && (
                    <div className="mt-3">
                      <div className="text-xs text-gray-500">
                        Abonnement par puissance souscrite (€/an, par kVA)
                      </div>
                      <div className="mt-1 grid grid-cols-3 gap-2 sm:grid-cols-5">
                        {Object.entries(e.abonnement_par_kva).map(([kva, val]) => (
                          <label key={kva} className="text-sm">
                            <span className="text-gray-500">{kva} kVA</span>
                            <input
                              type="number"
                              step="0.01"
                              value={val}
                              onChange={(ev) =>
                                patchKva(e.key, kva, Number(ev.target.value))
                              }
                              className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
                            />
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </section>

            <div className="mt-6 flex items-center gap-3">
              <button
                type="button"
                disabled={save.isPending}
                onClick={() => save.mutate()}
                className="rounded bg-blue-900 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-40"
              >
                {save.isPending ? "Enregistrement…" : "Enregistrer les prix"}
              </button>
              <button
                type="button"
                disabled={reset.isPending || !data.is_override}
                onClick={() => reset.mutate()}
                className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              >
                Réinitialiser (valeurs YAML)
              </button>
              {save.isSuccess && (
                <span className="text-sm text-green-700">Enregistré ✓</span>
              )}
            </div>
            <p className="mt-3 text-xs text-gray-400">
              Les nouveaux prix s'appliquent au prochain calcul de DPE (recalcul
              nécessaire pour les estimations déjà générées).
            </p>
          </>
        )}
      </div>
    </Layout>
  );
}
